import banner from "../../../courses/module-1/img4.png";

const modulo4 = {
  courseId: "finanzas-personales",
  id: "modulo-4",
  title: "📘 MÓDULO 4: Uso Responsable del Crédito",
  estimatedDuration: "2 horas",
  format: "Lectura + actividad de análisis + evaluación práctica",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos del módulo",
      content: [
        "Entender qué es el crédito y cómo utilizarlo con responsabilidad.",
        "Conocer el funcionamiento del buró de crédito y su impacto en tu historial financiero.",
        "Aprender a identificar créditos peligrosos o abusivos.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Lectura principal",
      title: "📖 Lectura principal: ¿Qué es el crédito y cómo se usa bien?",
      content: [
        {
          type: "paragraph",
          text: "📌 ¿Qué es el crédito?",
        },
        {
          type: "paragraph",
          text: "El crédito es dinero que una institución (como un banco o tienda) te presta con el compromiso de que lo devolverás en un plazo definido y con un interés adicional.",
        },
        "En palabras simples:",
        {
          type: "paragraph",
          text: "El crédito no es dinero extra, es dinero prestado que te costará más si no lo usas con cuidado.",
        },
      ],
    },
    {
      id: "util",
      navLabel: "¿Puede ser útil?",
      title: "✅ ¿Cuándo puede ser útil?",
      content: [
        {
          type: "list",
          items: [
            "Para compras grandes que no puedes pagar de golpe (una laptop, un refrigerador).",
            "Para emergencias reales cuando no tienes un fondo de ahorro.",
            "Para construir historial crediticio, si sabes usarlo responsablemente.",
          ],
        },
      ],
    },
    {
      id: "problema",
      navLabel: "¿Puede ser un problema?",
      title: "❌ ¿Cuándo puede ser un problema?",
      content: [
        {
          type: "list",
          items: [
            "Cuando lo usas para gastar más de lo que ganas.",
            "Cuando solo pagas el “mínimo” cada mes.",
            "Cuando lo usas como extensión de tu ingreso (vivir del crédito).",
            "Cuando no sabes cuánto te costará al final (intereses, comisiones, penalizaciones).",
          ],
        },
        {
          type: "paragraph",
          text: "Muchos jóvenes terminan atrapados en deudas desde temprano por falta de información, no por maldad o irresponsabilidad.",
        },
      ],
    },
    {
      id: "complementaria",
      navLabel: "Lectura complementaria",
      title: "📖 Lectura complementaria: ¿Qué es el buró de crédito?",
      content: [
        {
          type: "paragraph",
          text: "El Buró de Crédito es una institución que lleva un registro de cómo usas el crédito: si pagas a tiempo, si te atrasas, si tienes deudas activas, etc.",
        },
        {
          type: "list",
          items: [
            "Si tu historial es positivo: te dan acceso a mejores créditos con tasas bajas.",
            "Si tu historial es negativo: te pueden negar préstamos o cobrarte más intereses.",
            'Si no tienes historial: es como ser "invisible" para el sistema financiero.',
          ],
        },
        {
          type: "paragraph",
          text: "Tener historial no es malo, lo malo es tener un mal historial.",
        },
      ],
    },
    {
      id: "identificacion",
      navLabel: "Identificar créditos",
      title: "🛠 Cómo identificar créditos peligrosos",
      content: [
        {
          type: "list",
          items: [
            "CAT (Costo Anual Total): porcentaje real del costo del crédito (incluye intereses + comisiones).",
            "Interés mensual y anual: mientras más bajo, mejor.",
            "Comisiones ocultas: por apertura, mantenimiento, pagos tardíos, etc.",
            "Condiciones del contrato: tiempos, penalizaciones, cargos automáticos.",
            "La fuente: evita créditos que suenen demasiado buenos o provengan de cuentas dudosas por redes sociales.",
          ],
        },
        {
          type: "paragraph",
          text: "Pro tip: Si no entiendes los términos, no firmes nada todavía. Pregunta, investiga o pide ayuda.",
        },
      ],
    },
    {
      id: "guia",
      navLabel: "Mini guía",
      title: "💬 Mini guía: buen uso del crédito",
      content: [
        {
          type: "list",
          items: [
            "Paga siempre el total, no solo el mínimo.",
            "No uses más del 30% del límite de tu tarjeta.",
            "Activa alertas de pago en tu celular.",
            "No combines deuda con más deuda.",
          ],
        },
        {
          type: "link",
          href: "https://www.burodecredito.com.mx/",
          text: "Puedes consultar gratis tu buró 1 vez al año",
        },
      ],
    },
    {
      id: "extra",
      navLabel: "Extra",
      title: "📊 Extra: La Ley de Pareto y el uso del crédito",
      content: [
        {
          type: "paragraph",
          text: "📌 ¿Qué es la Ley de Pareto? También conocida como la regla del 80/20, la Ley de Pareto dice que una pequeña parte de causas suele generar la mayor parte de efectos.",
        },
        {
          type: "paragraph",
          text: "🔗 ¿Cómo se conecta con el uso del crédito? En tu vida financiera:",
        },
        {
          type: "list",
          items: [
            "El 20% de tus decisiones con el crédito puede generar el 80% de tus problemas o beneficios.",
            "Un solo mal uso (ej. pagar tarde, usar todo el límite) puede causarte la mayoría del estrés o deuda.",
            "Pero también, una sola acción inteligente (ej. pagar a tiempo siempre) puede darte la mayor parte de los beneficios.",
          ],
        },
        {
          type: "list",
          items: [
            "Identifica el 20% de hábitos que más afectan tu salud financiera.",
            "Cambia solo una o dos cosas clave primero.",
            "Obtén el mayor impacto con pocos movimientos.",
          ],
        },
        {
          type: "paragraph",
          text: "💬 “No se trata de hacer mucho, sino de hacer lo que realmente importa.”",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Actividad práctica",
      title: "📝 Evaluación: Actividad práctica",
      content: [
        {
          type: "paragraph",
          text: "Actividad: Evaluar y comentar una oferta real de tarjeta de crédito",
        },
        "¿Qué debes hacer?",
        {
          type: "paragraph",
          text: "1.Busca una tarjeta de crédito real (en el sitio web de un banco o tienda departamental).",
        },
        {
          type: "paragraph",
          text: "2.Copia la información principal:",
        },
        {
          type: "list",
          items: [
            "Límite de crédito inicial",
            "CAT promedio",
            "Interés anual",
            "Comisiones",
            "Requisitos para obtenerla",
          ],
        },
        {
          type: "paragraph",
          text: "3.Analiza y responde:",
        },
        {
          type: "list",
          items: [
            "¿Esta tarjeta me conviene a mí como estudiante? ¿Por qué?",
            "¿Qué peligros veo si no la uso bien?",
            "¿Qué alternativas más seguras podría considerar?",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografía",
      title: "📚 Bibliografía recomendada",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista: Finanzas para jóvenes con futuro”.",
        "CONDUSEF – “Guía sobre tarjetas de crédito”.",
        "Educación Financiera Banxico – Curso gratuito para jóvenes.",
        "Blog y canal YouTube: “Querido Dinero” (MX).",
      ],
    },
    {
      id: "frase",
      navLabel: "Frase del día",
      title: "🧠 Frase del día",
      content: [
        "“El crédito no es malo. Lo malo es usarlo sin saber lo que realmente cuesta.”",
      ],
    },
  ],
};

export default modulo4;
